import { createIcons, icons } from 'lucide';

// Helper to get icon HTML for rating
function getRatingIcon(rating: string) {
  const ratingLower = rating.toLowerCase();
  if (ratingLower.includes('high')) return `<span class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800"><i data-lucide="zap" class="h-3 w-3 mr-1"></i> High</span>`;
  if (ratingLower.includes('medium')) return `<span class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800"><i data-lucide="battery-medium" class="h-3 w-3 mr-1"></i> Medium</span>`;
  return `<span class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800"><i data-lucide="battery-low" class="h-3 w-3 mr-1"></i> Low</span>`;
}

// Helper to get icon HTML for work type
function getWorkTypeIcon(type: string) {
  const typeLower = type.toLowerCase();
  if (typeLower.includes('operational')) return `<span class="text-gray-500 inline-flex items-center" title="Operational"><i data-lucide="settings" class="h-4 w-4 mr-1"></i> Ops</span>`;
  if (typeLower.includes('meeting')) return `<span class="text-theme-500 inline-flex items-center" title="Meeting"><i data-lucide="users" class="h-4 w-4 mr-1"></i> Meet</span>`;
  if (typeLower.includes('strategic')) return `<span class="text-purple-500 inline-flex items-center" title="Strategic"><i data-lucide="compass" class="h-4 w-4 mr-1"></i> Strat</span>`;
  return `<span class="text-gray-500 inline-flex items-center"><i data-lucide="circle" class="h-4 w-4 mr-1"></i> ${type}</span>`;
}

// Run on both events to ensure it catches the page load
document.addEventListener('turbo:load', () => {
  console.log('Snapshot Plan: turbo:load fired');
  setupPlanPage();
});

document.addEventListener('DOMContentLoaded', () => {
  console.log('Snapshot Plan: DOMContentLoaded fired');
  setupPlanPage();
});

function setupPlanPage() {
    const generateBtn = document.getElementById("generate-plan-btn") as HTMLButtonElement;
    const container = document.getElementById("plan-container");
    
    if (!generateBtn || !container) {
      console.log('Snapshot Plan: Button or container not found (this is normal on other pages)');
      return;
    }

    if (generateBtn.dataset.listenerAttached === "true") {
      console.log('Snapshot Plan: Listener already attached');
      return;
    }
    
    console.log('Snapshot Plan: Attaching listener to button');
    generateBtn.dataset.listenerAttached = "true";
    
    generateBtn.addEventListener("click", async () => {
      console.log('Snapshot Plan: Generate button clicked');
      generateBtn.disabled = true;
      generateBtn.innerHTML = '<i data-lucide="loader-2" class="animate-spin -ml-0.5 mr-1.5 h-5 w-5"></i> Generating...';
      createIcons({ icons });
      
      container.innerHTML = `
        <div class="flex flex-col items-center justify-center py-12">
          <i data-lucide="loader-2" class="animate-spin h-12 w-12 text-theme-600 mb-4"></i>
          <p class="text-lg text-gray-600">Analyzing work items and generating strategy...</p>
          <p class="text-sm text-gray-400 mt-2">This may take up to 30 seconds.</p>
        </div>
      `;
      createIcons({ icons });

      try {
        const csrfToken = document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content;
        
        const response = await fetch(window.location.href, {
          method: "POST",
          headers: {
            "X-CSRF-Token": csrfToken || "",
            "Content-Type": "application/json"
          }
        });
        
        if (!response.ok) {
          const errorText = await response.text();
          throw new Error(`Failed to generate plan: ${response.status} ${errorText}`);
        }
        
        const data = await response.json();
        renderPlan(data.plan, container);
        
      } catch (error: any) {
        console.error(error);
        container.innerHTML = `
          <div class="bg-red-50 p-4 rounded-md">
            <div class="flex">
              <div class="flex-shrink-0">
                <i data-lucide="x-circle" class="h-5 w-5 text-red-400"></i>
              </div>
              <div class="ml-3">
                <h3 class="text-sm font-medium text-red-800">Error generating plan</h3>
                <div class="mt-2 text-sm text-red-700">
                  <p>${error.message || "Unknown error"}</p>
                </div>
              </div>
            </div>
          </div>
        `;
        createIcons({ icons });
      } finally {
        generateBtn.disabled = false;
        generateBtn.innerHTML = '<i data-lucide="sparkles" class="-ml-0.5 mr-1.5 h-5 w-5"></i> Regenerate Plan';
        createIcons({ icons });
      }
    });
}

function renderPlan(planItems: any[], container: HTMLElement) {
  if (!planItems || planItems.length === 0) {
    container.innerHTML = '<div class="p-6 text-center text-gray-500">No actionable insights found.</div>';
    return;
  }
  
  const html = planItems.map((item, index) => {
    const workItemsHtml = item.work_items && item.work_items.length > 0
      ? `
        <div class="overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg">
          <table class="min-w-full divide-y divide-gray-300">
            <thead class="bg-gray-50">
              <tr>
                <th scope="col" class="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6">Person</th>
                <th scope="col" class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Task</th>
                <th scope="col" class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 w-24">Monthly</th>
                <th scope="col" class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 w-24">Type</th>
                <th scope="col" class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 w-24">Engagement</th>
                <th scope="col" class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 w-24">Segment</th>
              </tr>
            </thead>
            <tbody class="divide-y divide-gray-200 bg-white">
              ${item.work_items.map((w: any) => `
                <tr class="hover:bg-gray-50">
                  <td class="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-6">${w.user_name}</td>
                  <td class="px-3 py-4 text-sm text-gray-500">${w.task}</td>
                  <td class="whitespace-nowrap px-3 py-4 text-sm font-medium text-gray-900">${w.monthly_hours}h</td>
                  <td class="whitespace-nowrap px-3 py-4 text-sm text-gray-500">${getWorkTypeIcon(w.work_type)}</td>
                  <td class="whitespace-nowrap px-3 py-4 text-sm text-gray-500">${getRatingIcon(w.rating)}</td>
                  <td class="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                    <span class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                      ${w.segment || '-'}
                    </span>
                  </td>
                </tr>
              `).join('')}
            </tbody>
          </table>
        </div>
      `
      : `<div class="text-sm text-gray-500 italic">No specific work items listed</div>`;

    return `
    <div class="bg-white shadow sm:rounded-lg overflow-hidden">
      <div class="px-4 py-5 sm:px-6 bg-gray-50 border-b border-gray-200 flex justify-between items-center">
        <h3 class="text-lg font-medium leading-6 text-gray-900">
          ${index + 1}. ${item.title}
        </h3>
        ${item.monthly_hours_impact ? `<span class="inline-flex items-center px-3 py-1 rounded-full text-sm font-bold bg-theme-600 text-white shadow-sm"><i data-lucide="clock" class="h-4 w-4 mr-1.5"></i> Impact: ${item.monthly_hours_impact} hrs/mo</span>` : ''}
      </div>
      <div class="px-4 py-5 sm:p-6">
        <div class="prose max-w-none text-gray-500 mb-6 whitespace-pre-wrap">
          ${item.description}
        </div>
        
        <div class="mt-6">
          <h4 class="text-sm font-medium text-gray-900 mb-3">Related Work Items (${item.work_items ? item.work_items.length : 0})</h4>
          ${workItemsHtml}
        </div>
      </div>
    </div>
  `}).join("");
  
  container.innerHTML = html;
  // Re-initialize icons for the new content
  createIcons({ icons });
}
